/**
 * CanvasManager — Manages the Fabric.js canvas instance.
 * Handles initialisation, zoom/pan, and resize.
 *
 * The canvas operates at logical template dimensions (e.g. 1600×300).
 * CSS transform is used only for fitting the canvas into the viewport.
 */
import * as fabric from 'fabric';

export class CanvasManager {
  /**
   * @param {string} canvasElementId - The ID of the <canvas> element
   * @param {object} [options]
   * @param {number} [options.width=1600]
   * @param {number} [options.height=300]
   */
  constructor(canvasElementId, options = {}) {
    this.width = options.width || 1600;
    this.height = options.height || 300;
    this._zoom = 1;
    this._minZoom = 0.1;
    this._maxZoom = 3;

    /** @type {fabric.Canvas} */
    this.canvas = new fabric.Canvas(canvasElementId, {
      width: this.width,
      height: this.height,
      backgroundColor: '#1a1a2e',
      preserveObjectStacking: true,
      selection: false,
      renderOnAddRemove: true,
    });

    this._viewportEl = document.getElementById('canvas-viewport');
    this._wrapperEl = document.getElementById('canvas-wrapper');

    this._fitToViewport();
    this._setupResizeObserver();
  }

  /**
   * Set the canvas to a new size (template dimensions).
   * @param {number} width
   * @param {number} height
   */
  setSize(width, height) {
    this.width = width;
    this.height = height;
    this.canvas.setDimensions({ width, height });
    this.canvas.renderAll();
    this._fitToViewport();
  }

  /**
   * Fit the canvas into the viewport with appropriate zoom.
   */
  _fitToViewport() {
    if (!this._viewportEl) return;

    const vpWidth = this._viewportEl.clientWidth - 48;
    const vpHeight = this._viewportEl.clientHeight - 48;

    if (vpWidth <= 0 || vpHeight <= 0) return;

    const scaleX = vpWidth / this.width;
    const scaleY = vpHeight / this.height;
    const scale = Math.min(scaleX, scaleY, 1);

    this._setZoom(scale);
  }

  /**
   * Apply zoom level.
   * @param {number} zoom
   */
  _setZoom(zoom) {
    this._zoom = Math.max(this._minZoom, Math.min(this._maxZoom, zoom));

    if (this._wrapperEl) {
      this._wrapperEl.style.transform = `scale(${this._zoom})`;
      this._wrapperEl.style.transformOrigin = 'center center';
    }

    this._updateZoomLabel();
  }

  /**
   * Update the zoom percentage label in the UI.
   */
  _updateZoomLabel() {
    const label = document.getElementById('zoom-level');
    if (label) {
      label.textContent = `${Math.round(this._zoom * 100)}%`;
    }
  }

  /**
   * Zoom in by a step.
   */
  zoomIn() {
    this._setZoom(this._zoom * 1.15);
  }

  /**
   * Zoom out by a step.
   */
  zoomOut() {
    this._setZoom(this._zoom / 1.15);
  }

  /**
   * Fit canvas to viewport.
   */
  fitToView() {
    this._fitToViewport();
  }

  /**
   * Get current zoom level.
   * @returns {number}
   */
  getZoom() {
    return this._zoom;
  }

  /**
   * Setup a resize observer to refit canvas when viewport changes.
   */
  _setupResizeObserver() {
    if (!this._viewportEl || !window.ResizeObserver) return;

    this._resizeObserver = new ResizeObserver(() => {
      this._fitToViewport();
    });
    this._resizeObserver.observe(this._viewportEl);
  }

  /**
   * Remove all objects from the canvas.
   */
  clear() {
    this.canvas.clear();
    this.canvas.backgroundColor = '#1a1a2e';
    this.canvas.renderAll();
  }

  /**
   * Get the Fabric canvas at full resolution for export.
   * @param {number} [targetWidth]
   * @param {number} [targetHeight]
   * @returns {HTMLCanvasElement}
   */
  toCanvasElement(targetWidth, targetHeight) {
    const multiplier = targetWidth ? targetWidth / this.width : 1;
    return this.canvas.toCanvasElement(multiplier);
  }

  /**
   * Export as data URL.
   * @param {object} [options]
   * @param {string} [options.format='png']
   * @param {number} [options.quality=1]
   * @param {number} [options.multiplier=1]
   * @returns {string}
   */
  toDataURL(options = {}) {
    return this.canvas.toDataURL({
      format: options.format || 'png',
      quality: options.quality || 1,
      multiplier: options.multiplier || 1,
    });
  }

  /**
   * Render all objects.
   */
  renderAll() {
    this.canvas.renderAll();
  }

  /**
   * Dispose of the canvas and cleanup.
   */
  dispose() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
    this.canvas.dispose();
  }
}
